import {FC, FormEvent, useEffect, useMemo, useState} from "react";
import {useWarehouseLocks, WarehouseLockNotice} from "../../warehouse/useWarehouseLocks";
import {RRDayType} from "../../types/RRDayType";
import {
    Alert,
    Button,
    CloseButton,
    Col,
    Form,
    FormCheck,
    FormControl, FormGroup, FormLabel,
    FormSelect,
    Modal,
    Row, Spinner,
    Tab,
    Tabs
} from "react-bootstrap";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {
    selectDepartment,
    selectCurrentEmployee,
    selectEditCalendarEvent,
    selectSelectedEmployees,
    setEditCalendarEvent
} from "../../../features/AppStateSlice";
import {CancelIconButton, ConfirmIconButton, DeleteIconButton} from "../../components/IconButtons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck} from "@fortawesome/free-solid-svg-icons";
import {selectCalendar, selectCurrentEmployees, selectEvents} from "../../../features/AppDataSlice";
import ReactDatePicker from "react-datepicker";
import {Maybe, MaybeDate, MaybeString} from "../../types/Maybe";
import {
    checkOverlapTimes,
    dateTimeToString, dateToString,
    dateWithZeroTime,
    showErrorsListInToast,
    showMsgInToast,
    stringToDate,
    timeToString
} from "../../tools";
import {RRCalendarDayType} from "../../types/RRCalendarDayType";
import {collection, getDocs, query, updateDoc, where} from "firebase/firestore";
import {createCalendarPlan} from "../../calendar/scheduleMutations";
import {eventPeriodError} from "../../calendar/eventPeriod";
import {appFirestore} from "../../../index";
import {RREmployeeType} from "../../types/RREmployeeType";
import {calculateWorkhours} from "../../../firebase/FirebaseEmployeeStatisticsLoader";
import {forEach} from "react-bootstrap/ElementChildren";
import {RREventType} from "../../types/RREventType";
import {
    deleteCalendarItemsWithChangeLog,
    updateCalendarItemsWithChangeLog,
} from "./change-log/calendarChangeLog";

interface Props {
}

const EditCalendarEvent: FC<Props> = (props) => {
    const dispatch = useAppDispatch();
    const allCalendarEvents = useAppSelector(selectCalendar);
    const allEvents = useAppSelector(selectEvents);
    const department = useAppSelector(selectDepartment);
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const editCalendarEvent = useAppSelector(selectEditCalendarEvent);
    const events = editCalendarEvent?.calendarDay?.events || [];
    const employees = useAppSelector(selectCurrentEmployees);
    const selectedEmployees = useAppSelector(selectSelectedEmployees);
    const [employee, setEmployee] = useState<Maybe<RREmployeeType>>(null);
    const [dayType, setDayType] = useState<string | undefined>("ON");
    const [startTime, setStartTime] = useState<MaybeDate>(editCalendarEvent?.item?.eventDayStart || new Date());
    const [eventId, setEventId] = useState<MaybeString>(editCalendarEvent?.item?.eventId);
    const [onDescription, setOnDescription] = useState<MaybeString>();
    const [isWarehouse, setIsWarehouse] = useState(false);
    const [isMontage, setIsMontage] = useState(false);
    const [isSupervisor, setIsSupervisor] = useState(false);
    const [working, setWorking] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);
    const warehouseStatus = useWarehouseLocks();
    const originalAccess = warehouseStatus(editCalendarEvent?.item?.eventDayStart,
        editCalendarEvent?.item?.eventId || "", editCalendarEvent?.item?.department || department);
    const targetAccess = warehouseStatus(startTime, dayType === "ON" ? eventId || events[0]?.id || "" : "");
    const lockAccess = originalAccess.blocked ? originalAccess : targetAccess;
    const isReadOnly = (editCalendarEvent?.isReadOnly ?? false) || lockAccess.blocked;

    const validateData = () => {
        if (isReadOnly) {
            return false;
        }
        const newErrors: string[] = [];
        if (dayType === "ON" && startTime) {
            const selectedEvent = allEvents.data.find(value => value.id === (eventId || events[0]?.id));
            if (!selectedEvent) newErrors.push("Избраното събитие не е налично. Презаредете календара.");
            else if (selectedEvent.id !== "WAREHOUSE") {
                const periodError = eventPeriodError(startTime, selectedEvent.eventName, selectedEvent.firstDate, selectedEvent.lastDate);
                if (periodError) newErrors.push(periodError);
            }
        }
        const emplIds = selectedEmployees.length ? selectedEmployees : [editCalendarEvent?.employee?.id || ""];

        emplIds.forEach(
            emplId => {

                const calendarEvents = allCalendarEvents?.data?.filter(
                    e => e && e.employeeId === emplId &&
                        e.eventId !== editCalendarEvent?.event?.id
                ) || [];
                calendarEvents.forEach(
                    calEvent => {
                        const event = events.find(
                            e => e && calEvent.eventDayEnd &&
                                startTime &&
                                checkOverlapTimes(
                                    startTime,
                                    editCalendarEvent?.item?.eventDayEnd || startTime,
                                    calEvent.eventDayStart,
                                    calEvent.eventDayEnd
                                )
                        );

                        if (event && !event.isDeleted && calEvent.eventId !== "WAREHOUSE" && editCalendarEvent?.event?.id !== "WAREHOUSE") {
                            const event = events.find(e => e.id === calEvent.eventId);
                            const eventName = event ? event.eventName : "неизвестно събитие";
                            newErrors.push(`За този час служителят е планиран за събитието: ${eventName}`);
                        }
                    }
                );
                if (startTime && editCalendarEvent?.item?.eventDayEnd) {
                    if (startTime > editCalendarEvent.item.eventDayEnd) {
                        newErrors.push("Началния час не може да бъде по-късно от крайния час");
                    }
                }
                setErrors(newErrors);
            }
        );

        return (newErrors.length === 0);
    }

    useEffect(() => {
        setEmployee(editCalendarEvent?.employee || (employees && employees.length > 0 ? employees[0] : null));
        setDayType((!editCalendarEvent?.item || editCalendarEvent?.item?.dayType === "WORKDAY") ? "ON" : "OFF")
        setEventId(editCalendarEvent?.item?.eventId || editCalendarEvent?.event?.id);
        setOnDescription(editCalendarEvent?.item?.description);
        setIsWarehouse(editCalendarEvent?.item?.isWarehouse || false);
        setIsSupervisor(editCalendarEvent?.item?.isSupervisor || false);
        setIsMontage(editCalendarEvent?.item?.isMontage || false);
        setErrors([]);

        const date = stringToDate(editCalendarEvent?.calendarDay.date) || new Date();
        if (editCalendarEvent?.item?.eventDayStart) {
            date.setHours(editCalendarEvent.item.eventDayStart.getHours());
            date.setMinutes(editCalendarEvent.item.eventDayStart.getMinutes());
        } else {
            if (editCalendarEvent?.event) {
                date.setHours(editCalendarEvent.event.firstDate?.getHours() || 0);
                date.setMinutes(editCalendarEvent.event.firstDate?.getMinutes() || 0);
            }
        }
        date.setSeconds(0);
        setStartTime(date)

        setShowConfirmDelete(false);
    }, [editCalendarEvent])

    if (!editCalendarEvent) return null;

    function hideModal() {
        dispatch(setEditCalendarEvent(null))
    }

    function changeContext(selectedEventId: string) {
        const selectedEvent = events.find(value => value.id === selectedEventId);
        return {
            eventNumber: selectedEvent?.eventNumber || selectedEventId,
            eventName: selectedEvent?.eventName || "",
        };
    }

    async function handleFormSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (isReadOnly || working) return;
        const fields = new FormData(e.currentTarget);
        const offReason = String(fields.get("offReason") || "");
        const offNotes = String(fields.get("offNotes") || "");

        if (events.length === 0) {
            showErrorsListInToast("Възникна грешка", ["Не сте избрали събитие"]);
            return
        }

        if (!employee) {
            showErrorsListInToast("Възникна грешка", ["Не сте избрали служител"]);
            return
        }

        if (dayType === "OFF" && !["HOLIDAY", "DAYOFF", "SICK"].includes(offReason)) {
            showErrorsListInToast("Възникна грешка", ["Не сте избрали причина за отсъствието на служителя"]);
            return
        }


        if (!validateData()) return;

        try {
            setWorking(true);

            type CalendarWrite = Pick<RRCalendarDayType, "eventDayStart" | "eventDayEnd" | "eventId" |
                "description" | "employeeId" | "employeeName" | "isWarehouse" | "isMontage" |
                "isSupervisor" | "face" | "notes" | "department"> & {dayType: RRDayType; isHoliday: boolean};
            let data: CalendarWrite;
            if (dayType === "ON") data = {
                dayType: "WORKDAY",
                eventDayStart: startTime || new Date(),
                eventDayEnd: editCalendarEvent?.item?.eventDayEnd || null,
                eventId: eventId || events[0].id!,
                description: onDescription || "",
                employeeId: employee.id!,
                employeeName: employee.name!,
                isWarehouse: isWarehouse,
                isMontage: isMontage,
                isHoliday: false,
                isSupervisor: isSupervisor,
                face: editCalendarEvent?.item?.face || "",
                notes: editCalendarEvent?.item?.notes || "",
                department: department,
            }
            else {
                let dayEnd: Date | null = null;
                if (offReason === "HOLIDAY") {
                    dayEnd = new Date(startTime || new Date());
                    dayEnd.setHours(dayEnd.getHours() + 8);
                }

                data = {
                    dayType: offReason === "HOLIDAY" ? "HOLIDAY" : offReason === "SICK" ? "SICK" : "DAYOFF",
                    eventDayStart: startTime || new Date(),
                    eventDayEnd: dayEnd,
                    eventId: "",
                    description: offNotes,
                    employeeId: employee.id!,
                    employeeName: employee.name!,
                    isWarehouse: false,
                    isMontage: false,
                    isSupervisor: false,
                    isHoliday: true,
                    face: "",
                    notes: "",
                    department: department,
                }
            }

            if (editCalendarEvent?.item?.ref) {
                await updateCalendarItemsWithChangeLog([{
                    ref: editCalendarEvent.item.ref,
                    data,
                    context: changeContext(data.eventId),
                }], currentEmployee);
            } else {
                await createCalendarPlan(data);
            }

            if (editCalendarEvent?.event?.eventStatus === "ПОТВЪРДЕНО") {
                await updateDoc(editCalendarEvent!.event!.ref!, {eventStatus: "ПОДГОТВЕНО"})
            }

            if (dayType === "ON") {
                const eventStart = dateWithZeroTime(data.eventDayStart)!;
                const eventEnd = new Date(eventStart);
                eventEnd.setDate(eventEnd.getDate() + 1);
                const calndarEvents = await getDocs(query(collection(appFirestore, "calendar"),
                    where("eventId", "==", data.eventId),
                    where("eventDayStart", ">=", eventStart),
                    where("eventDayStart", "<", eventEnd)));

                for (const employeeId of selectedEmployees) {
                        const emplEvent = calndarEvents.docs.find(d => d.get("employeeId") === employeeId &&
                            (data.eventId !== "WAREHOUSE" || (d.get("department") || "montage") === department));
                        const empl = employees.find(e => e.id === employeeId);
                        if (!empl) throw new Error("Избран служител вече не е наличен в този отдел.");
                        if (empl) {
                            if (emplEvent) {
                                const newData = {
                                    ...emplEvent.data(),
                                    employeeId: employeeId,
                                    employeeName: empl.name,
                                    eventDayStart: data.eventDayStart,
                                    description: data.description,
                                    isSupervisor: data.isSupervisor,
                                    isWarehouse: data.isWarehouse,
                                    isMontage: data.isMontage,
                                    isHoliday: data.isHoliday,
                                }
                                await updateCalendarItemsWithChangeLog([{
                                    ref: emplEvent.ref,
                                    data: newData,
                                    context: changeContext(data.eventId),
                                }], currentEmployee);
                            } else {
                                const newData = {
                                    ...data,
                                    employeeId: employeeId,
                                    employeeName: empl.name
                                };
                                await createCalendarPlan(newData);
                            }
                        }
                }

            }

            // if(data.eventDayEnd) {
            //     await calculateWorkhours(data.eventDayEnd, [data.employeeId])
            // }

            dispatch(setEditCalendarEvent(null));
            showMsgInToast("Данните са запазени успешно!");
        } catch (e) {
            console.error(e);
            showErrorsListInToast("Възникна проблем при запазването на данните:",
                [e instanceof Error ? e.message : "Неуспешен запис.", "При групова операция проверете кои записи са запазени."])
        } finally {
            setWorking(false);
        }
    }

    async function deleteEvent() {
        if (isReadOnly || working) return;
        if (editCalendarEvent?.item?.ref) {
            try {
                setWorking(true);
                await deleteCalendarItemsWithChangeLog([{
                    ref: editCalendarEvent.item.ref,
                    context: changeContext(editCalendarEvent.item.eventId),
                }], currentEmployee);
                if (editCalendarEvent.item.eventDayEnd) {
                    // await calculateWorkhours(editCalendarEvent.item.eventDayEnd, [editCalendarEvent.item.employeeId])
                }
                setWorking(false);
                hideModal();
            } catch (e) {
                showErrorsListInToast("Възникна грешка", [e instanceof Error ? e.message : "Неуспешно изтриване."])
            } finally {
                setWorking(false);
                setShowConfirmDelete(false);
            }
        }
    }

    function updateStartTime(e: Date | null) {
        if (!e || !startTime) return;

        const date = new Date(startTime);
        date.setHours(e.getHours());
        date.setMinutes(e.getMinutes());
        date.setSeconds(0);
        setStartTime(date);
    }

    function isSameDay(eventDayStart: Date, eventDayEnd: MaybeDate | undefined, date: string) {
        const start = dateToString(dateWithZeroTime(eventDayStart))!;
        if (start === date) return true;
        if (eventDayEnd) {
            const end = dateToString(dateWithZeroTime(eventDayEnd))!;
            if (end === date) return true;
        }
        return false;
    }

    // Същите записи определят забраната и обяснението за нея, включително скритите за деня събития.
    const holidayEmployeeIds = selectedEmployees.length ? selectedEmployees : [editCalendarEvent.employee?.id || ""];
    const holidayConflicts = allCalendarEvents.data.filter(item =>
        holidayEmployeeIds.includes(item.employeeId) && item.dayType === "WORKDAY" &&
        isSameDay(item.eventDayStart, item.eventDayEnd, editCalendarEvent.calendarDay.date));

    return (
        <Modal show size={"lg"}>
            <Modal.Header>
                <Modal.Title className={"w-100"}>
                    <Row className={"justify-content-between align-content-between"}>
                        <Col xs={"auto"}>{editCalendarEvent?.employee?.name || "- Не е избран служител -"}</Col>
                        <Col className={"text-primary"}>{editCalendarEvent.calendarDay.date}</Col>
                    </Row>
                </Modal.Title>
                <CloseButton onClick={() => hideModal()}/>
            </Modal.Header>
            <Form onSubmit={e => handleFormSubmit(e)}>
                <Modal.Body>
                    <WarehouseLockNotice access={lockAccess}/>
                    {
                        !editCalendarEvent?.employee &&
                        <Row className={"mb-4 pb-4 bg-secondary-light"}>
                            <Col>
                                <FormGroup>
                                    <FormLabel className={"mb-3 fw-bold"}>Изберете служител:</FormLabel>
                                    <FormSelect value={employee?.id || undefined}
                                                onChange={event => setEmployee(employees.find(empl => empl.id === event.target.value))}>
                                        {
                                            employees.map(
                                                value => <option key={value.id}
                                                                 value={value.id}> {value.name} </option>
                                            )
                                        }
                                    </FormSelect>
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    <Tabs activeKey={dayType} onSelect={eKey => setDayType(eKey?.toString())}>
                        <Tab title={"Работен ден"} eventKey={"ON"}
                             disabled={(editCalendarEvent?.isReadOnly ?? false) || originalAccess.blocked}>
                            <Form.Group className={"mt-3"}>
                                <Form.Label className={"mb-3 fw-bold"}>Изберете събитие:</Form.Label>
                                <FormSelect value={eventId || undefined}
                                            onChange={event => setEventId(event.target.value)}
                                            disabled={(editCalendarEvent?.isReadOnly ?? false) || originalAccess.blocked}
                                >
                                    {
                                        events.map(
                                            value => <option key={value.id}
                                                             value={value.id}> {value.id === "WAREHOUSE" && department === "store" ? "Кухня" : value.eventName} </option>
                                        )
                                    }
                                </FormSelect>
                            </Form.Group>

                            <Form.Group className={"mt-3"}>
                                <Form.Label className={"mb-3 fw-bold"}>Опции:</Form.Label>
                                <Row className={"flex-nowrap bg-secondary-light p-1 lh-1"}>
                                    <Col>
                                        <Row className={"flex-nowrap"}>
                                            <Col xs={"auto"} className={"lh-2 me-0 pe-0"}>Начален час:</Col>
                                            <Col xs={"auto"}>
                                                <ReactDatePicker onChange={e => {
                                                    updateStartTime(e)
                                                }}
                                                                 disabled={isReadOnly}
                                                                 showTimeSelect
                                                                 showTimeSelectOnly
                                                                 timeIntervals={30}
                                                                 timeCaption="Начало:"
                                                                 dateFormat="HH:mm"
                                                                 className={"form-control-sm w-50px"}
                                                                 value={timeToString(startTime)}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col>
                                        <FormCheck label={"Супервайзор"} inline
                                                   disabled={isReadOnly}
                                                   className={"pt-1"}
                                                   checked={isSupervisor}
                                                   onChange={event => setIsSupervisor(event.target.checked)}
                                        />
                                    </Col>
                                    {
                                        department === "montage" &&
                                        <Col>
                                            <FormCheck label={"Супервайзор склад"} inline
                                                         disabled={isReadOnly}
                                                       className={"pt-1"}
                                                       checked={isWarehouse}
                                                       onChange={event => setIsWarehouse(event.target.checked)}
                                            /></Col>
                                    }
                                    <Col>
                                        <FormCheck label={"Служител"} inline
                                                    disabled={isReadOnly}
                                                   className={"pt-1"}
                                                   checked={isMontage}
                                                   onChange={event => setIsMontage(event.target.checked)}
                                        /></Col>
                                </Row>
                            </Form.Group>
                            {
                                errors.length > 0 &&
                                <Row className={"mt-2 p-0"}>
                                    <Col xs={12}>
                                        <Alert variant={"danger"}>
                                            <ul className={"mb-0"}>
                                                {
                                                    errors.map((error, index) => <li key={index}>{error}</li>)
                                                }
                                            </ul>
                                        </Alert>
                                    </Col>
                                </Row>
                            }
                            <Form.Group>
                                <Form.Label className={"mb-3 fw-bold"}>Бележки:</Form.Label>
                                <FormControl as={"textarea"} rows={3} value={onDescription || ""}
                                             disabled={isReadOnly}
                                             onChange={event => setOnDescription(event.target.value)}
                                />
                            </Form.Group>


                        </Tab>
                        <Tab title={"Неработен ден"} eventKey={"OFF"}
                             disabled={(editCalendarEvent?.isReadOnly ?? false) || originalAccess.blocked}>
                            {
                                holidayConflicts.length === 0 ?
                                    <>
                                        <Form.Group className={"mt-3"}>
                                            <Form.Label className={"mb-3 fw-bold"}>Посочете причина:</Form.Label>
                                            <Row className={"flex-nowrap bg-secondary-light p-2 lh-1"}>
                                                <Col><FormCheck label={"Отсъства по болест"} name={"offReason"}
                                                                disabled={isReadOnly}
                                                                type={"radio"}
                                                                value={"SICK"}
                                                                defaultChecked={editCalendarEvent?.item?.dayType === "SICK"}
                                                                inline/></Col>
                                                <Col><FormCheck label={"Планирана отпуска"} name={"offReason"}
                                                                disabled={isReadOnly}
                                                                type={"radio"}
                                                                value={"HOLIDAY"}
                                                                defaultChecked={editCalendarEvent?.item?.dayType === "HOLIDAY"}
                                                                inline/></Col>
                                                <Col><FormCheck label={"По други причини"} name={"offReason"}
                                                                disabled={isReadOnly}
                                                                type={"radio"}
                                                                value={"DAYOFF"}
                                                                defaultChecked={editCalendarEvent?.item?.dayType === "DAYOFF"}
                                                                inline/></Col>
                                            </Row>
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label className={"mb-3 fw-bold"}>Бележки:</Form.Label>
                                            <FormControl as={"textarea"} rows={3} name={"offNotes"}
                                                            disabled={isReadOnly}
                                                         defaultValue={editCalendarEvent?.item?.description || ""}/>
                                        </Form.Group>
                                    </>
                                    :
                                    <Alert variant={"danger"}>
                                        <div>{selectedEmployees.length
                                            ? "За един или повече от избраните служители вече има планиран работен ден:"
                                            : "За този служител вече има планиран работен ден:"}</div>
                                        <ul className={"mb-0 mt-2"}>
                                            {holidayConflicts.map(item => {
                                                const event = allEvents.data.find(value => value.id === item.eventId);
                                                const eventName = item.eventId === "WAREHOUSE"
                                                    ? (department === "store" ? "Кухня" : "Склад")
                                                    : event?.eventName || "Събитието не е налично";
                                                return <li key={item.id}>
                                                    {selectedEmployees.length > 0 && <><strong>{item.employeeName}</strong> — </>}
                                                    <strong>{eventName}{event?.eventNumber ? ` №${event.eventNumber}` : ""}</strong>
                                                    <div>Планирана работа: {dateToString(item.eventDayStart)} от {timeToString(item.eventDayStart)}
                                                        {item.eventDayEnd ? ` до ${dateTimeToString(item.eventDayEnd)}` : " (без краен час)"}.</div>
                                                    {event?.firstDate && <div>Дата на събитието: {dateToString(event.firstDate)}
                                                        {event.lastDate ? ` – ${dateToString(event.lastDate)}` : ""}.</div>}
                                                </li>;
                                            })}
                                        </ul>
                                    </Alert>
                            }
                        </Tab>
                    </Tabs>

                </Modal.Body>

                <Modal.Footer>
                    {
                        working ?
                            <Col xs={12}><Spinner/></Col>
                            :
                            <>
                                {
                                !isReadOnly &&
                                <Col>
                                    <Button type={"submit"} variant={"primary"}>
                                        <FontAwesomeIcon icon={faCheck}/>&nbsp;
                                        Запази {selectedEmployees.length ? "за всички избрани" : ""}</Button>
                                </Col>
                            }
                                <Col className={"text-end"}>
                                    {
                                        showConfirmDelete ?
                                            <ConfirmIconButton onClick={() => deleteEvent()}
                                                               onReject={() => setShowConfirmDelete(false)}/>
                                            :
                                            <>
                                                {
                                                    !isReadOnly && editCalendarEvent?.item?.ref &&
                                                    <DeleteIconButton onClick={() => setShowConfirmDelete(true)}/>
                                                }
                                                &nbsp;
                                                <CancelIconButton onClick={() => hideModal()}/>
                                            </>
                                    }
                                </Col>
                            </>
                    }
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

export default EditCalendarEvent;
